import { useApi } from '@/config/apiFetcher'
import log from '@/utils/useLogger'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef, type Ref } from 'vue'
import { appStore } from '@/store/app'
import { storeToRefs } from 'pinia'
import decrypt from '@/utils/decrypt'
import type { deployData } from '@/types/devices.types'

export default () => {
  const api = useApi()
  const queryClient = useQueryClient()
  const deployDataUrl = `${import.meta.env.VITE_DEPLOY_DATA}`
  const { selectedDock } = storeToRefs(appStore())

  const saveDeployCode = () => {
    return useMutation({
      mutationFn: async ({ code, deviceId }: { code: string; deviceId: string }) => {
        try {
          const res = await api.post(`${deployDataUrl}/deploy-data/code`, {
            code,
            deviceId,
          })
          return res.data
        } catch (error) {
          log.error('Error saving deploy code:', error)
          throw error
        }
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: ['deploy-code', variables.deviceId] })
      },
    })
  }
  /**
   * Fetches the saved encrypted deploy code for a device and returns the
   * decrypted deploy payload, or null when no code is stored (or it
   * fails to decrypt). Used by the device list to redeploy a dock
   * without re-prompting for the code (FLY3D-1015).
   */
  const fetchDeployData = async (deviceId: string): Promise<deployData | null> => {
    try {
      const res = await api.get(`${deployDataUrl}/deploy-data/${deviceId}`)
      if (!res.data.code) {
        log.error('No deploy code found')
        return null
      }
      const decrypted = await decrypt(res.data.code)
      if (decrypted) {
        return JSON.parse(decrypted) as deployData
      }
      return null
    } catch (error) {
      log.error('Error getting deploy code:', error)
      return null
    }
  }

  /**
   * Fetches the legacy encrypted deploy code for the selected dock and
   * returns the decrypted Tailscale machine name.
   *
   * Devices onboarded via the new wizard (DeviceOnboarding.vue) have
   * `machine_name` stored directly on the device object, so callers
   * should pass an `enabled` ref that's `false` when the device object
   * already supplies a machine name — that way we skip the network
   * round-trip for newly onboarded docks.
   */
  const getDeployCode = (enabled?: Ref<boolean> | ComputedRef<boolean>) => {
    return useQuery({
      queryKey: computed(() => ['deploy-code', selectedDock.value]),
      queryFn: async () => (await fetchDeployData(selectedDock.value))?.machine ?? null,
      enabled: computed(() => {
        const hasDock = !!selectedDock.value.trim() && selectedDock.value.trim() !== '0000'
        if (!hasDock) return false
        return enabled ? enabled.value : true
      }),
      staleTime: 1000 * 60 * 2,
      gcTime: 1000 * 60 * 10,
      retry: 2,
      retryDelay: 1000,
    })
  }
  return {
    saveDeployCode,
    getDeployCode,
    fetchDeployData,
  }
}
